import { HomeCareLeadId, homeCarePartnerDescriptor } from "@sp-crm/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    partnerSyncPollIntervalOf,
    PartnerSyncStatus,
} from "components/partner-sync/partner-sync-display";
import { PartnerSyncIndicator } from "components/partner-sync/partner-sync-indicator";
import {
    PartnerSyncNow,
    usePartnerSyncClock,
} from "components/partner-sync/partner-sync-status";
import { trpc } from "components/rpc/trpc";
import React, { useMemo } from "react";
import { ExternalRecordLink } from "./external-record-link";

const wellSkySendsInput = (homeCareLeadId: HomeCareLeadId) => ({
    homeCareLeadId,
    integration: "wellSkyPersonalCare" as const,
});

export const useHomeCareLeadWellSkySyncNow = (
    homeCareLeadId: HomeCareLeadId,
): PartnerSyncNow => {
    const trpcContext = trpc.useTRPC();
    const queryClient = useQueryClient();
    const { mutate, isPending, error } = useMutation({
        ...trpcContext.homeCareLeadPartnerSend.syncNow.mutationOptions(),
        onSuccess: sync => {
            queryClient.setQueryData(
                trpcContext.homeCareLeadPartnerSend.sends.queryKey(
                    wellSkySendsInput(homeCareLeadId),
                ),
                previous => (previous ? { ...previous, sync } : previous),
            );
        },
    });
    return useMemo(
        () => ({
            run: () => mutate({ homeCareLeadId }),
            isPending,
            error: error ? error.message : null,
        }),
        [mutate, isPending, error, homeCareLeadId],
    );
};

interface WellSkySync {
    sync: PartnerSyncStatus | null;
    dataUpdatedAt: number;
}

const useHomeCareLeadWellSkySync = (homeCareLeadId: HomeCareLeadId): WellSkySync => {
    const trpcContext = trpc.useTRPC();
    const query = useQuery({
        ...trpcContext.homeCareLeadPartnerSend.sends.queryOptions(
            wellSkySendsInput(homeCareLeadId),
        ),
        refetchInterval: current =>
            current.state.status === "error"
                ? false
                : partnerSyncPollIntervalOf(current.state.data?.sync ?? null, Date.now()),
    });
    return { sync: query.data?.sync ?? null, dataUpdatedAt: query.dataUpdatedAt };
};

interface HomeCareLeadWellSkyLinkProps {
    homeCareLeadId: HomeCareLeadId;
    url: string;
}

export const HomeCareLeadWellSkyLink: React.FC<HomeCareLeadWellSkyLinkProps> = ({
    homeCareLeadId,
    url,
}) => {
    const { sync, dataUpdatedAt } = useHomeCareLeadWellSkySync(homeCareLeadId);
    const syncNow = useHomeCareLeadWellSkySyncNow(homeCareLeadId);
    const now = usePartnerSyncClock(dataUpdatedAt);
    const { partnerName } = homeCarePartnerDescriptor("wellSkyPersonalCare");
    if (!sync) {
        return <ExternalRecordLink url={url} partnerName={partnerName} />;
    }
    return (
        <PartnerSyncIndicator
            partnerName={partnerName}
            url={url}
            status={sync}
            syncNow={syncNow}
            now={now}
        />
    );
};
